import './KpiSetup.scss';
import { useEffect, useRef, useState } from 'react';
import { MdDragHandle } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { Page } from '@Components/Page';
import { Intent } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { DEFAULT_KPIS, KpiRow } from './kpi-setup-constants';
import { getKpisToSave, getUndefinedKpiKeys, reorderKpis } from './kpi-setup-utils';
import { saveCompanyKpis, useCompanyIds, useCompanyKpis } from './use-kpi-setup';

export const KpiSetup = () => {
  const setError = useSetError();
  const [company, setCompany] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [kpiItems, setKpiItems] = useState<KpiRow[]>(DEFAULT_KPIS);
  const [originalKpiItems, setOriginalKpiItems] = useState<KpiRow[]>([]);
  const [undefinedKpiKeys, setUndefinedKpiKeys] = useState<string[]>([]);
  const [kpisPulled, setKpisPulled] = useState(false);

  const { data: companyIds = [], error: companyIdsError } = useCompanyIds();
  const {
    data: fetchedKpis,
    error: kpisError,
    isFetching: fetchingKpis,
    refetch: fetchKpis
  } = useCompanyKpis(company);

  useEffect(() => {
    if (!companyIdsError) return;
    setError({
      title: 'Internal Error',
      message: 'Could not fetch existing company ids',
      reportError: companyIdsError
    });
  }, [companyIdsError, setError]);

  useEffect(() => {
    if (!kpisError) return;
    setError({
      title: 'Internal Error',
      message: 'Could not fetch kpis for company',
      reportError: kpisError
    });
  }, [kpisError, setError]);

  useEffect(() => {
    if (!fetchedKpis) return;
    setOriginalKpiItems(fetchedKpis);
    setUndefinedKpiKeys(getUndefinedKpiKeys(fetchedKpis));
    setKpiItems(fetchedKpis);
    setKpisPulled(true);
  }, [fetchedKpis]);

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const handleSort = () => {
    const fromIndex = dragItem.current;
    const toIndex = dragOverItem.current;
    if (fromIndex === null || toIndex === null) return;
    setKpiItems(items => reorderKpis(items, fromIndex, toIndex));
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleGetKpis = () => {
    if (!company) {
      setError({ message: 'Please select a valid company', title: 'Error: Company Name' });
      return;
    }
    fetchKpis();
  };

  const handleSaveChanges = async () => {
    if (!company) {
      setError({ message: 'Please select a valid company', title: 'Error: Company Name' });
      return;
    }
    if (!kpisPulled) {
      setError({
        message: 'Please pull valid KPIs',
        title: 'Error: KPIs do not match selected company'
      });
      return;
    }

    setSaving(true);
    try {
      const kpisToSend = getKpisToSave(kpiItems, originalKpiItems, undefinedKpiKeys);
      await saveCompanyKpis(company, kpisToSend);
      setSaving(false);
      await setError({
        intent: Intent.SUCCESS,
        title: 'Success!',
        message: 'Successfully reorder KPIs'
      });
      window.location.reload();
    } catch (e) {
      setSaving(false);
      setError({
        title: 'Internal Error',
        message: 'Could not update kpi ordering',
        reportError: e
      });
    }
  };

  return (
    <Page
      title='KPI Setup'
      pageType='KPI Setup'
      actions={
        <Button
          data-testid='save-changes-button'
          design={Intent.SUCCESS}
          loading={saving}
          onClick={handleSaveChanges}
        >
          Save Changes
        </Button>
      }
    >
      <div className='kpi-setup-page'>
        <div className='company-picker' data-testid='company-picker'>
          <Dropdown
            searchable
            className='company-select'
            placeholder='Select from existing companies...'
            options={companyIds}
            value={company}
            onChange={setCompany}
          />
          <Button
            data-testid='get-kpis-button'
            className='kpi-button'
            loading={fetchingKpis}
            onClick={handleGetKpis}
          >
            Get KPIs
          </Button>
        </div>

        <div className='list-container' data-testid='kpi-list'>
          {kpiItems.map((item, index) => (
            <KpiListItem
              key={item.kpi}
              item={item}
              onDragStart={() => {
                dragItem.current = index;
              }}
              onDragEnter={() => {
                dragOverItem.current = index;
              }}
              onDragEnd={handleSort}
            />
          ))}
        </div>
      </div>
    </Page>
  );
};

interface KpiListItemProps {
  item: KpiRow;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDragEnd: () => void;
}

const KpiListItem = ({ item, onDragStart, onDragEnter, onDragEnd }: KpiListItemProps) => (
  <div
    className='list-item'
    draggable
    onDragStart={onDragStart}
    onDragEnter={onDragEnter}
    onDragEnd={onDragEnd}
    onDragOver={e => e.preventDefault()}
  >
    <MdDragHandle />
    <span className='kpi-item'>{`${item.kpi} | ${item.name}`}</span>
  </div>
);
